import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { MfaStep } from '../components/account/MfaStep';

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Divider component
const OrDivider = () => (
  <div className="relative my-6">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-neutral-200" />
    </div>
    <div className="relative flex justify-center text-sm">
      <span className="px-4 bg-white text-neutral-400">or continue with</span>
    </div>
  </div>
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, loginWithGoogle, completeMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mfaToken, setMfaToken] = useState(location.state?.mfaToken || null);

  const from = location.state?.from?.pathname || '/';

  const handleMfa = async (code) => {
    setLoading(true);
    try {
      goAfterLogin(await completeMfa(mfaToken, code));
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Invalid code');
      if (error.response?.status === 401) setMfaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const goAfterLogin = (userData) => {
      toast.success('Welcome back!');
      
      // Redirect based on user role
      // Installers go directly to /installer, not the main site
      if (userData?.role_id === 'role_installer') {
        navigate('/installer', { replace: true });
        return;
      }
      
      // Users with is_admin=true OR a role_id should go to admin panel
      const hasAdminAccess = userData?.is_admin || userData?.role_id;
      
      if (hasAdminAccess) {
        navigate('/admin', { replace: true });
      } else {
        // Regular customers go to their dashboard or previous page
        navigate(from === '/' ? '/dashboard' : from, { replace: true });
      }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password, rememberMe);
      if (userData?.requires_2fa) {
        setMfaToken(userData.mfa_token);
        setPassword('');
        return;
      }
      goAfterLogin(userData);
    } catch (error) {
      const rawDetail = error.response?.data?.detail;
      const errorDetail = (typeof rawDetail === 'object' && rawDetail?.message) || rawDetail || 'Invalid credentials';

      // Check for specific error types
      if (rawDetail?.code === 'password_reset_required') {
        toast.error(errorDetail, { duration: 10000 });
      } else if (error.response?.status === 429) {
        toast.error(errorDetail); // Account locked message
      } else if (error.response?.status === 403) {
        // Email verification required
        toast.error(errorDetail);
        navigate('/verify-email-pending', { state: { email } });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      loginWithGoogle();
    } catch (error) {
      toast.error('Google sign-in unavailable. Your browser\'s privacy settings may be blocking it. Please use email/password login instead.');
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Toaster position="bottom-right" />
      <div className="flex flex-col justify-center px-8 py-12 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-12 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="mb-8">
            <Link to="/" className="font-serif text-2xl font-semibold">Apollo Shades</Link>
            <h1 className="text-3xl font-serif mt-6 mb-2">Welcome back</h1>
            <p className="text-neutral-500">Sign in to access your saved designs and quotes.</p>
          </div>
          
          {mfaToken ? (
            <MfaStep onSubmit={handleMfa} onCancel={() => setMfaToken(null)} loading={loading} />
          ) : (<>
          {/* Google Sign In Button */}
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleGoogleLogin}
            className="w-full h-12 flex items-center justify-center gap-3 border-neutral-300 hover:bg-neutral-50"
            data-testid="google-login-btn"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </Button>
          
          <OrDivider />
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label className="label-overline">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12" data-testid="email-input" />
            </div>
            <div>
              <Label className="label-overline">Password</Label>
              <div className="relative mt-2">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 pr-12" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={rememberMe} 
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-neutral-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-amber-600 hover:text-amber-700 hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="login-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <p className="text-center text-neutral-500 mt-8">
            Don't have an account? <Link to="/register" className="text-neutral-900 font-medium hover:underline">Create one</Link>
          </p>
          </>)}
        </motion.div>
      </div>
      <div className="hidden lg:block relative bg-neutral-100">
        <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80" alt="Interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-900/20" />
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, company);
      toast.success('Account created! Please check your email to verify your account.');
      // Redirect to verification pending page
      navigate('/verify-email-pending', { state: { email } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      loginWithGoogle();
    } catch (error) {
      toast.error('Google sign-in unavailable. Your browser\'s privacy settings may be blocking it. Please use email/password login instead.');
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Toaster position="bottom-right" />
      <div className="flex flex-col justify-center px-8 py-12 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-12 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="mb-8">
            <Link to="/" className="font-serif text-2xl font-semibold">Apollo Shades</Link>
            <h1 className="text-3xl font-serif mt-6 mb-2">Create an account</h1>
            <p className="text-neutral-500">Save your designs and track your quotes.</p>
          </div>
          
          {/* Google Sign Up Button */}
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleGoogleLogin}
            className="w-full h-12 flex items-center justify-center gap-3 border-neutral-300 hover:bg-neutral-50"
            data-testid="google-register-btn"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </Button>
          
          <OrDivider />
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-overline">Full Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-2 h-12" data-testid="name-input" />
              </div>
              <div>
                <Label className="label-overline">Company</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} className="mt-2 h-12" />
              </div>
            </div>
            <div>
              <Label className="label-overline">Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12" data-testid="email-input" />
            </div>
            <div>
              <Label className="label-overline">Password *</Label>
              <div className="relative mt-2">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} className="h-12 pr-12" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} />
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="register-submit">
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          <p className="text-center text-neutral-500 mt-8">
            Already have an account? <Link to="/login" className="text-neutral-900 font-medium hover:underline">Sign in</Link>
          </p>
        </motion.div>
      </div>
      <div className="hidden lg:block relative bg-neutral-100">
        <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80" alt="Interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-900/20" />
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send reset email');
      }
      
      setSubmitted(true);
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Toaster position="bottom-right" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-serif font-semibold mb-4">Check your email</h1>
          <p className="text-neutral-600 mb-8" data-testid="forgot-generic-message">
            If an account exists for <span className="font-medium">{email}</span>, we've sent a password reset link. It can be used once and expires in 30 minutes.
          </p>
          <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">
            Back to login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Toaster position="bottom-right" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
        <div className="mb-8">
          <Link to="/" className="font-serif text-2xl font-semibold">Apollo Shades</Link>
          <h1 className="text-3xl font-serif mt-6 mb-2">Reset your password</h1>
          <p className="text-neutral-500">Enter your email address and we'll send you instructions to reset your password.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
          <div>
            <Label className="label-overline">Email</Label>
            <Input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              className="mt-2 h-12" 
              placeholder="you@example.com"
              data-testid="forgot-email-input" 
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="forgot-submit">
            {loading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Get token from URL, then confirm it is still valid (unused, not expired)
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const [linkError, setLinkError] = useState('');
  const [purpose, setPurpose] = useState('forgot');
  const isInvite = purpose === 'invite';
  React.useEffect(() => {
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/check?token=${encodeURIComponent(token)}`)
      .then(async (r) => { const d = await r.json(); if (!r.ok) setLinkError(d.detail || 'This reset link is invalid.'); else setPurpose(d.purpose || 'forgot'); })
      .catch(() => {});
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 10) {
      toast.error('Password must be at least 10 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password, confirm_password: confirmPassword })
      });
      
      if (!response.ok) {
        const data = await response.json();
        if (/used|expired|invalid/i.test(data.detail || '')) setLinkError(data.detail);
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
      toast.success(isInvite ? 'Password set!' : 'Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || linkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Toaster position="bottom-right" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-serif font-semibold mb-4">Invalid Reset Link</h1>
          <p className="text-neutral-600 mb-8" data-testid="reset-link-error">
            {linkError || 'This password reset link is invalid or has expired.'}
          </p>
          <Link to="/forgot-password" className="text-amber-600 hover:text-amber-700 font-medium">
            Request a new reset link
          </Link>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Toaster position="bottom-right" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-serif font-semibold mb-4">{isInvite ? 'Password Set!' : 'Password Reset!'}</h1>
          <p className="text-neutral-600 mb-8">
            {isInvite ? 'Your account is ready. Redirecting you to sign in...' : 'Your password has been reset successfully. Redirecting to login...'}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Toaster position="bottom-right" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <div className="mb-8">
          <Link to="/" className="font-serif text-2xl font-semibold">Apollo Shades</Link>
          <h1 className="text-3xl font-serif mt-6 mb-2" data-testid="reset-page-title">{isInvite ? 'Set your password' : 'Set new password'}</h1>
          <p className="text-neutral-500">{isInvite ? 'Choose a password to activate your team account. Use at least 10 characters.' : 'Enter your new password below.'}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
          <div>
            <Label className="label-overline">New Password</Label>
            <div className="relative mt-2">
              <Input 
                type={showPassword ? 'text' : 'password'} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                className="h-12 pr-12" 
                placeholder="At least 10 characters"
                data-testid="new-password-input" 
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <PasswordStrengthMeter password={password} />
          </div>
          <div>
            <Label className="label-overline">Confirm Password</Label>
            <Input 
              type={showPassword ? 'text' : 'password'} 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              required 
              className="mt-2 h-12" 
              placeholder="Confirm your password"
              data-testid="confirm-password-input" 
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="reset-submit">
            {loading ? 'Saving...' : (isInvite ? 'Set Password' : 'Reset Password')}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};
